import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import AutoStatusToast from '../common/AutoStatusToast';
import { getClassMemberDisplayNumbers } from '../../utils/classMemberNumbers';
import MemberProfileThumb from '../common/MemberProfileThumb';
import { SeatRotateIcon } from '../common/SeatRotateIcon';
import { useAutoStatusToast } from '../../hooks/useAutoStatusToast';
import { saveAssignmentPointsNote } from '../../services/assignmentPointsNoteService';
import {
  requestAssignmentPointsNoteFromMainTab,
  setLiveAssignmentPointsNoteDoc,
  shouldApplyRemoteAssignmentPointsNote,
  subscribeAssignmentPointsNoteDoc,
} from '../../utils/assignmentPointsNoteTabBridge';
import { loadAssignmentPointsNoteCache } from '../../utils/assignmentPointsNoteStorage';
import type {
  AssignmentCoverImage,
  AssignmentPointsNoteDoc,
  AssignmentStatusValue,
  AssignmentStudentStatus,
  GeneralAssignmentItem,
  ProjectAssignmentItem,
  RoutineAssignmentItem,
} from '../../types/assignmentPointsNote';
import { emptyAssignmentPointsNoteDoc } from '../../types/assignmentPointsNote';
import {
  calculateRoutineDates,
  ensureRoutineDateBuckets,
  pointsForStatus,
  upsertStatus,
} from '../../utils/assignmentPointsNoteProgress';
import {
  INCOMPLETE_ROUTINE_BADGE_BG,
  formatIncompleteReportDate,
  getRoutineTodayDate,
  incompleteKindBadgeStyle,
  type IncompleteAssignmentCard,
  type IncompleteStatusTarget,
} from '../../utils/assignmentIncompleteReport';
import {
  emptyGradeLayout,
  loadClassMemberGradeLayoutFromStorage,
  type ClassMemberGroupMeta,
} from '../../utils/classMembersStorage';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import type { FlatMember } from './GeneralAssignmentSettingsModal';
import {
  loadNoteMemberLayoutMode,
  loadNoteSelectedGroupId,
  resolveNoteSelectedGroupId,
  saveNoteMemberLayoutMode,
  saveNoteSelectedGroupId,
} from '../../utils/noteSelectedGroupPreference';
import { subscribeSelectedClassGroupChanged } from '../../utils/selectedClassGroupPreference';
import NoteGroupSelector from './NoteGroupSelector';
import CachedAssignmentCoverImage from '../common/CachedAssignmentCoverImage';
import { preloadAssignmentCoverImagesFromCache } from '../../utils/assignmentCoverImageCache';

type FilterTab = 'allIncomplete' | 'general' | 'project' | 'routine';
type ListFilter = 'all' | 'incomplete';
type ViewMode = 'number' | 'seat';

type AssignmentChip = {
  key: string;
  kind: 'general' | 'project' | 'routine';
  itemId: string;
  subTaskId?: string;
  date?: string;
  title: string;
  projectTitle?: string;
  dueDate: string;
  color: string;
  basePoints: number;
  assignedMemberIds: string[];
  projectCreatedAt?: string;
  subTaskOrder?: number;
  coverImageId?: string;
};

type PendingChange = {
  status: AssignmentStatusValue;
  points: number;
  memo?: string;
};

const STATUS_LEGEND: {
  value: AssignmentStatusValue;
  label: string;
  num: number;
  color: string;
}[] = [
  { value: 'pending', label: '미확인', num: 1, color: '#FF316B' },
  { value: 'confirmed', label: '확인', num: 2, color: '#227971' },
  { value: 'delayed', label: '지연확인', num: 3, color: '#65A994' },
  { value: 'exempted', label: '예외', num: 4, color: '#FFCC6E' },
];

const STATUS_BTN_LABEL: Partial<Record<AssignmentStatusValue, string>> = {
  pending: '미제출',
  confirmed: '확인',
  delayed: '지연',
  exempted: '예외',
};

const STATUS_ORDER: AssignmentStatusValue[] = [
  'pending',
  'confirmed',
  'delayed',
  'exempted',
];

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const COMPLETED: AssignmentStatusValue[] = ['confirmed', 'delayed', 'exempted'];
const SEAT_EMPTY_TOAST = '자리배치된 멤버가 없습니다. 번호 보기를 사용하세요.';

type Props = {
  workspaceId: string;
  gradeId: string;
};

function toIsoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayIso(): string {
  return toIsoLocal(new Date());
}

function recentDateIsos(n: number): string[] {
  const out: string[] = [];
  const base = new Date();
  for (let i = 0; i < n; i += 1) {
    const d = new Date(base);
    d.setDate(base.getDate() - i);
    out.push(toIsoLocal(d));
  }
  return out;
}

function formatDateMenuLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  if (iso === todayIso()) return '오늘';
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${DAY_LABELS[d.getDay()]})`;
}

function formatChipDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function formatNum(n: number): string {
  return String(n).padStart(2, '0');
}

function isCompletedStatus(status: AssignmentStatusValue | undefined): boolean {
  return status != null && COMPLETED.includes(status);
}

function cellKey(memberId: string, chipKey: string): string {
  return `${memberId}::${chipKey}`;
}

function intersectIds(assigned: string[], groupIds: Set<string>): string[] {
  return assigned.filter((id) => groupIds.has(id));
}

function routineRunsOnDate(item: RoutineAssignmentItem, date: string): boolean {
  const schedule = calculateRoutineDates(item.startDate, item.dueDate, item.routineDays);
  if (schedule.includes(date)) return true;
  return item.dateStatuses.some((b) => b.date === date);
}

function readGeneralStatus(
  item: GeneralAssignmentItem,
  memberId: string
): AssignmentStudentStatus | undefined {
  return item.studentStatuses.find((s) => s.studentId === memberId);
}

function readProjectStatus(
  item: ProjectAssignmentItem,
  subTaskId: string,
  memberId: string
): AssignmentStudentStatus | undefined {
  return item.studentStatusesBySubTask[`${subTaskId}:${memberId}`];
}

function readRoutineStatus(
  item: RoutineAssignmentItem,
  date: string,
  memberId: string
): AssignmentStudentStatus | undefined {
  const ensured = ensureRoutineDateBuckets(item);
  const bucket = ensured.dateStatuses.find((b) => b.date === date);
  return bucket?.studentStatuses.find((s) => s.studentId === memberId);
}

function chipIsIncomplete(
  chip: AssignmentChip,
  getStatus: (memberId: string, chip: AssignmentChip) => AssignmentStatusValue
): boolean {
  const ids = chip.assignedMemberIds;
  if (ids.length === 0) return false;
  return ids.some((id) => !isCompletedStatus(getStatus(id, chip)));
}

function flattenMembers(
  layout: ReturnType<typeof emptyGradeLayout>
): FlatMember[] {
  const groups = [...layout.groups].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
  );
  const out: FlatMember[] = [];
  for (const g of groups) {
    const rows = layout.membersByGroupId[g.id] ?? [];
    const nums = getClassMemberDisplayNumbers(rows);
    rows.forEach((m, i) => {
      out.push({
        ...m,
        orderIndex: nums[i] ?? i + 1,
        groupId: g.id,
        groupName: g.name?.trim() || '그룹',
      });
    });
  }
  return out;
}

function readPresentationAssignmentBootstrap(workspaceId: string, gradeId: string) {
  const cachedDoc = loadAssignmentPointsNoteCache(workspaceId, gradeId);
  const layout =
    loadClassMemberGradeLayoutFromStorage(workspaceId, gradeId) ?? emptyGradeLayout();
  const nextGroups = [...layout.groups].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
  );
  const doc = cachedDoc ?? emptyAssignmentPointsNoteDoc(workspaceId, gradeId);
  const selectedGroupId = resolveNoteSelectedGroupId(
    nextGroups.map((g) => g.id),
    [loadNoteSelectedGroupId('assignmentPoints', workspaceId, gradeId)]
  );
  return {
    doc,
    layout,
    groups: nextGroups,
    members: flattenMembers(layout),
    selectedGroupId,
    hasCache: !!cachedDoc,
  };
}

const AssignmentManagementCardPanel: React.FC<Props> = ({
  workspaceId,
  gradeId,
}) => {
  const { toastState, showSaving, showSuccess, showError, showInfo } =
    useAutoStatusToast(3000);

  /* 메인 프레젠 전용: 첫 페인트부터 LS 동기 표시 — 불러오는중으로 막지 않음 */
  const [boot] = useState(() =>
    readPresentationAssignmentBootstrap(workspaceId, gradeId)
  );
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [doc, setDoc] = useState<AssignmentPointsNoteDoc>(() => boot.doc);
  const [allMembers, setAllMembers] = useState<FlatMember[]>(() => boot.members);
  const [groups, setGroups] = useState<ClassMemberGroupMeta[]>(() => boot.groups);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(
    () => boot.selectedGroupId
  );

  useEffect(() => {
    return subscribeSelectedClassGroupChanged((detail) => {
      if (detail.workspaceId !== workspaceId.trim()) return;
      if (detail.gradeId !== gradeId.trim()) return;
      const ids = new Set(groups.map((g) => g.id));
      if (detail.groupId && ids.has(detail.groupId)) {
        setSelectedGroupId(detail.groupId);
      }
    });
  }, [workspaceId, gradeId, groups]);

  const [filterTab, setFilterTab] = useState<FilterTab>('allIncomplete');
  const [incompleteByMemberOpen, setIncompleteByMemberOpen] = useState(false);
  const [editingIncompleteCardKey, setEditingIncompleteCardKey] = useState<
    string | null
  >(null);
  const [listFilter, setListFilter] = useState<ListFilter>('all');
  const [categoryId, setCategoryId] = useState<string>('all');
  const [projectId, setProjectId] = useState<string>('all');
  const [routineDate, setRoutineDate] = useState<string>(() => todayIso());
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  const [selectedChipKey, setSelectedChipKey] = useState<string | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(
    () => new Set()
  );
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(
    () => new Map()
  );
  const [viewMode, setViewMode] = useState<ViewMode>('number');
  const [isSeatRotated, setIsSeatRotated] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!workspaceId || !gradeId) return;
    const saved = loadNoteMemberLayoutMode('assignmentPoints', workspaceId, gradeId);
    if (saved === 'number' || saved === 'seat') setViewMode(saved);
  }, [workspaceId, gradeId]);

  const selectedStudentsRef = useRef(selectedStudents);
  selectedStudentsRef.current = selectedStudents;
  const applyBatchRef = useRef<(status: AssignmentStatusValue) => void>(() => {});

  const gradeReady = Boolean(workspaceId && gradeId);

  const applyAssignmentLoad = useCallback(
    (nextDoc: AssignmentPointsNoteDoc, layout: ReturnType<typeof emptyGradeLayout>) => {
      const nextGroups = [...layout.groups].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id)
      );
      setDoc(nextDoc);
      setAllMembers(flattenMembers(layout));
      setGroups(nextGroups);
      setSelectedGroupId((prev) => {
        const next = resolveNoteSelectedGroupId(
          nextGroups.map((g) => g.id),
          [prev, loadNoteSelectedGroupId('assignmentPoints', workspaceId, gradeId)]
        );
        if (next) saveNoteSelectedGroupId('assignmentPoints', workspaceId, gradeId, next);
        return next;
      });
      setPendingChanges(new Map());
      setSelectedStudents(new Set());
      setSelectedChipKey(null);
      setLiveAssignmentPointsNoteDoc(nextDoc);
    },
    [workspaceId, gradeId]
  );

  const load = useCallback(() => {
    if (!gradeReady) {
      setDoc(emptyAssignmentPointsNoteDoc(workspaceId, gradeId));
      setAllMembers([]);
      setGroups([]);
      setSelectedGroupId(null);
      setLoading(false);
      return;
    }
    setLoadError(null);
    const boot = readPresentationAssignmentBootstrap(workspaceId, gradeId);
    applyAssignmentLoad(boot.doc, boot.layout);
    setLoading(false);
    if (!boot.hasCache) {
      /* 브릿지 대기로 UI를 막지 않음 — subscribe가 메인 탭 워밍 결과를 반영 */
      requestAssignmentPointsNoteFromMainTab({ workspaceId, gradeId });
    }
  }, [workspaceId, gradeId, gradeReady, applyAssignmentLoad]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingChangesRef = useRef(pendingChanges);
  pendingChangesRef.current = pendingChanges;

  useEffect(() => {
    if (!gradeReady) return undefined;
    return subscribeAssignmentPointsNoteDoc((remote) => {
      if (remote.workspaceId !== workspaceId || remote.gradeId !== gradeId) return;
      if (pendingChangesRef.current.size > 0) return;
      setDoc((local) => {
        if (
          !shouldApplyRemoteAssignmentPointsNote({
            remote,
            local,
            isDirty: false,
          })
        ) {
          return local;
        }
        setLiveAssignmentPointsNoteDoc(remote);
        return remote;
      });
    });
  }, [gradeReady, workspaceId, gradeId]);

  const members = useMemo(() => {
    if (!selectedGroupId) return [] as FlatMember[];
    return allMembers.filter((m) => m.groupId === selectedGroupId);
  }, [allMembers, selectedGroupId]);

  const groupIdSet = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const categories = doc.settings.categories ?? [];
  const coverImages = doc.coverImages ?? [];
  const coverImageById = useMemo(() => {
    const map = new Map<string, AssignmentCoverImage>();
    for (const img of coverImages) {
      map.set(img.id, img);
    }
    return map;
  }, [coverImages]);

  useEffect(() => {
    preloadAssignmentCoverImagesFromCache(coverImages);
  }, [coverImages]);
  const hasChanges = pendingChanges.size > 0;

  const memberGroupMap = useMemo(() => {
    const map = new Map<string, string>();
    allMembers.forEach((m) => {
      if (m.groupId) map.set(m.id, m.groupId);
    });
    return map;
  }, [allMembers]);

  const resolveBaseStatus = useCallback(
    (memberId: string, chip: AssignmentChip): AssignmentStudentStatus | undefined => {
      const item = doc.items.find((x) => x.id === chip.itemId);
      if (!item) return undefined;
      if (chip.kind === 'general' && item.kind === 'general') {
        return readGeneralStatus(item, memberId);
      }
      if (chip.kind === 'project' && item.kind === 'project' && chip.subTaskId) {
        return readProjectStatus(item, chip.subTaskId, memberId);
      }
      if (chip.kind === 'routine' && item.kind === 'routine' && chip.date) {
        return readRoutineStatus(item, chip.date, memberId);
      }
      return undefined;
    },
    [doc.items]
  );

  const resolveDisplayStatus = useCallback(
    (memberId: string, chip: AssignmentChip): AssignmentStatusValue => {
      const pending = pendingChanges.get(cellKey(memberId, chip.key));
      if (pending) return pending.status;
      return resolveBaseStatus(memberId, chip)?.status ?? 'pending';
    },
    [pendingChanges, resolveBaseStatus]
  );

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of doc.items) {
      if (item.kind !== 'project') continue;
      if (!intersectIds(item.assignedMemberIds, groupIdSet).length) continue;
      map.set(item.id, item.title || '제목 없음');
    }
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [doc.items, groupIdSet]);

  const routineDateOptions = useMemo(() => recentDateIsos(8), []);

  const chips = useMemo(() => {
    const statusOf = (memberId: string, chip: AssignmentChip): AssignmentStatusValue =>
      resolveBaseStatus(memberId, chip)?.status ?? 'pending';

    const catColor = (cid?: string) =>
      categories.find((c) => c.id === cid)?.color || '#a2a2a2';

    let list: AssignmentChip[] = [];

    const pushGeneral = () => {
      for (const item of doc.items) {
        if (item.kind !== 'general') continue;
        const assigned = intersectIds(item.assignedMemberIds, groupIdSet);
        if (assigned.length === 0) continue;
        list.push({
          key: `g:${item.id}`,
          kind: 'general',
          itemId: item.id,
          title: item.title || '제목 없음',
          dueDate: item.dueDate,
          color: catColor(item.categoryId),
          basePoints: item.basePoints,
          assignedMemberIds: assigned,
          coverImageId: item.coverImageId,
        });
      }
    };

    const pushProject = () => {
      for (const item of doc.items) {
        if (item.kind !== 'project') continue;
        const assigned = intersectIds(item.assignedMemberIds, groupIdSet);
        if (assigned.length === 0) continue;
        const subs = [...item.subTasks].sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        );
        for (const sub of subs) {
          list.push({
            key: `p:${item.id}:${sub.id}`,
            kind: 'project',
            itemId: item.id,
            subTaskId: sub.id,
            title: sub.title || '하위과제',
            projectTitle: item.title || '제목 없음',
            dueDate: item.dueDate,
            color: item.color || '#a2a2a2',
            basePoints: sub.points,
            assignedMemberIds: assigned,
            projectCreatedAt: item.createdAt,
            subTaskOrder: sub.order,
            coverImageId: item.coverImageId,
          });
        }
      }
    };

    const pushRoutine = (dateForRoutine: string) => {
      for (const item of doc.items) {
        if (item.kind !== 'routine') continue;
        const assigned = intersectIds(item.assignedMemberIds, groupIdSet);
        if (assigned.length === 0) continue;
        if (!routineRunsOnDate(item, dateForRoutine)) continue;
        const color =
          (item.categoryId
            ? categories.find((c) => c.id === item.categoryId)?.color
            : undefined) || INCOMPLETE_ROUTINE_BADGE_BG;
        list.push({
          key: `r:${item.id}:${dateForRoutine}`,
          kind: 'routine',
          itemId: item.id,
          date: dateForRoutine,
          title: item.title || '제목 없음',
          dueDate: dateForRoutine,
          color,
          basePoints: item.basePoints,
          assignedMemberIds: assigned,
          coverImageId: item.coverImageId,
        });
      }
    };

    if (filterTab === 'allIncomplete' || filterTab === 'general') pushGeneral();
    if (filterTab === 'allIncomplete' || filterTab === 'project') pushProject();
    if (filterTab === 'allIncomplete' || filterTab === 'routine') {
      pushRoutine(filterTab === 'allIncomplete' ? todayIso() : routineDate);
    }

    if (filterTab === 'general') {
      if (categoryId !== 'all') {
        list = list.filter((c) => {
          const item = doc.items.find((x) => x.id === c.itemId);
          return item?.kind === 'general' && item.categoryId === categoryId;
        });
      }
      list = list.filter((c) => c.kind === 'general');
      if (listFilter === 'incomplete') {
        list = list.filter((c) => chipIsIncomplete(c, statusOf));
      }
    } else if (filterTab === 'project') {
      list = list.filter((c) => c.kind === 'project');
      if (projectId !== 'all') {
        list = list.filter((c) => c.itemId === projectId);
      }
      if (listFilter === 'incomplete') {
        list = list.filter((c) => chipIsIncomplete(c, statusOf));
      }
    } else if (filterTab === 'routine') {
      list = list.filter((c) => c.kind === 'routine');
      if (listFilter === 'incomplete') {
        list = list.filter((c) => chipIsIncomplete(c, statusOf));
      }
    } else if (filterTab === 'allIncomplete') {
      list = list.filter((c) => chipIsIncomplete(c, statusOf));
    }

    const regular = list
      .filter((c) => c.kind === 'general')
      .sort((a, b) => (b.dueDate || '').localeCompare(a.dueDate || ''));
    const project = list
      .filter((c) => c.kind === 'project')
      .sort((a, b) => {
        if (a.itemId !== b.itemId) {
          const ta = a.projectCreatedAt ? Date.parse(a.projectCreatedAt) : 0;
          const tb = b.projectCreatedAt ? Date.parse(b.projectCreatedAt) : 0;
          if (tb !== ta) return tb - ta;
          return a.itemId.localeCompare(b.itemId);
        }
        return (b.subTaskOrder ?? 0) - (a.subTaskOrder ?? 0);
      });
    const routine = list
      .filter((c) => c.kind === 'routine')
      .sort((a, b) => (b.dueDate || '').localeCompare(a.dueDate || ''));

    return [...regular, ...project, ...routine];
  }, [
    categories,
    categoryId,
    doc.items,
    filterTab,
    groupIdSet,
    listFilter,
    projectId,
    resolveBaseStatus,
    routineDate,
  ]);

  const selectedChip = useMemo(
    () => chips.find((c) => c.key === selectedChipKey) ?? null,
    [chips, selectedChipKey]
  );

  useEffect(() => {
    if (selectedChipKey && !chips.some((c) => c.key === selectedChipKey)) {
      setSelectedChipKey(chips[0]?.key ?? null);
      setSelectedStudents(new Set());
    } else if (!selectedChipKey && chips.length > 0) {
      setSelectedChipKey(chips[0].key);
    }
  }, [chips, selectedChipKey]);

  /** 자리 모드: 포인트 아이템 페이지와 동일 — 바깥 trim, 빈 행·열 압축, 180° 회전 */
  const seatViewData = useMemo(() => {
    const seated = members.filter(
      (m) =>
        m.seatPosition &&
        typeof m.seatPosition.row === 'number' &&
        typeof m.seatPosition.col === 'number'
    );
    const unseated = members.filter(
      (m) =>
        !(
          m.seatPosition &&
          typeof m.seatPosition.row === 'number' &&
          typeof m.seatPosition.col === 'number'
        )
    );
    if (!seated.length) {
      return {
        positioned: [] as Array<{
          member: FlatMember;
          gridRow: number;
          gridCol: number;
        }>,
        unseated,
        rowTemplate: '',
        colTemplate: '',
      };
    }

    const GRID_TRACKS = 16;
    const ROTATE_MAX_INDEX = 14;
    const usedRows = new Set<number>();
    const usedCols = new Set<number>();

    const rotatedSeats = seated.map((member) => {
      const seatRow = member.seatPosition!.row;
      const seatCol = member.seatPosition!.col;
      const rotatedRow = isSeatRotated ? ROTATE_MAX_INDEX - seatRow : seatRow;
      const rotatedCol = isSeatRotated ? ROTATE_MAX_INDEX - seatCol : seatCol;
      [rotatedRow, rotatedRow + 1].forEach((r) => {
        if (r >= 0 && r < GRID_TRACKS) usedRows.add(r);
      });
      [rotatedCol, rotatedCol + 1].forEach((c) => {
        if (c >= 0 && c < GRID_TRACKS) usedCols.add(c);
      });
      return { member, rotatedRow, rotatedCol };
    });

    const trimStartRow = Math.min(...Array.from(usedRows));
    const trimEndRow = Math.max(...Array.from(usedRows));
    const trimStartCol = Math.min(...Array.from(usedCols));
    const trimEndCol = Math.max(...Array.from(usedCols));

    const rowTemplate = Array.from(
      { length: Math.max(1, trimEndRow - trimStartRow + 1) },
      (_, idx) => (usedRows.has(trimStartRow + idx) ? '1fr' : '0.5fr')
    ).join(' ');

    const colTemplate = Array.from(
      { length: Math.max(1, trimEndCol - trimStartCol + 1) },
      (_, idx) => (usedCols.has(trimStartCol + idx) ? '1fr' : '0.28fr')
    ).join(' ');

    const positioned = rotatedSeats.map(({ member, rotatedRow, rotatedCol }) => ({
      member,
      gridRow: rotatedRow - trimStartRow + 1,
      gridCol: rotatedCol - trimStartCol + 1,
    }));

    return { positioned, unseated, rowTemplate, colTemplate };
  }, [members, isSeatRotated]);

  const hasSeatedMembers = seatViewData.positioned.length > 0;

  useEffect(() => {
    if (viewMode !== 'seat') return;
    if (hasSeatedMembers) return;
    setViewMode('number');
    showInfo(SEAT_EMPTY_TOAST);
  }, [viewMode, hasSeatedMembers, showInfo]);

  const setMemberStatus = useCallback(
    (memberId: string, chip: AssignmentChip, newStatus: AssignmentStatusValue) => {
      if (!chip.assignedMemberIds.includes(memberId)) return;
      const ck = cellKey(memberId, chip.key);
      const existing = resolveBaseStatus(memberId, chip);
      const points = pointsForStatus(chip.basePoints, newStatus, doc.settings);
      const baseStatus = existing?.status ?? 'pending';
      setPendingChanges((prev) => {
        const next = new Map(prev);
        if (newStatus === baseStatus) {
          // 원래 상태로 되돌리면 pending 제거 (포인트만 다른 경우도 원복)
          const basePts = existing?.earnedPoints;
          if (basePts == null || basePts === points) {
            next.delete(ck);
            return next;
          }
        }
        next.set(ck, {
          status: newStatus,
          points,
          memo: existing?.memo,
        });
        return next;
      });
    },
    [doc.settings, resolveBaseStatus]
  );

  const chipFromIncompleteTarget = useCallback(
    (target: IncompleteStatusTarget): AssignmentChip | null => {
      const item = doc.items.find((x) => x.id === target.itemId);
      if (!item) return null;
      return {
        key: target.chipKey,
        kind: target.kind as 'general' | 'project' | 'routine',
        itemId: target.itemId,
        subTaskId: target.subTaskId,
        date: target.date,
        title: item.title || '',
        dueDate: target.date || ('dueDate' in item ? item.dueDate : ''),
        color: '#a2a2a2',
        basePoints: target.basePoints,
        assignedMemberIds: item.assignedMemberIds,
      };
    },
    [doc.items]
  );

  const resolveIncompleteTargetStatus = useCallback(
    (memberId: string, target: IncompleteStatusTarget): AssignmentStatusValue => {
      const pending = pendingChanges.get(cellKey(memberId, target.chipKey));
      if (pending) return pending.status;
      const chip = chipFromIncompleteTarget(target);
      if (!chip) return 'pending';
      return resolveBaseStatus(memberId, chip)?.status ?? 'pending';
    },
    [pendingChanges, chipFromIncompleteTarget, resolveBaseStatus]
  );

  /** pendingChanges까지 반영한 멤버별 미완료 (칩 단위 = 일반/하위과제/오늘 루틴) */
  const incompleteByMemberRows = useMemo(() => {
    const rows: Array<{
      memberId: string;
      orderIndex: number;
      memberName: string;
      cards: IncompleteAssignmentCard[];
    }> = [];

    for (const m of members) {
      const cards: IncompleteAssignmentCard[] = [];

      for (const item of doc.items) {
        if (!item.assignedMemberIds.includes(m.id)) continue;

        if (item.kind === 'general') {
          const target: IncompleteStatusTarget = {
            chipKey: `g:${item.id}`,
            kind: 'general',
            itemId: item.id,
            basePoints: item.basePoints,
          };
          const status = resolveIncompleteTargetStatus(m.id, target);
          if (isCompletedStatus(status)) continue;
          cards.push({
            itemId: item.id,
            kind: 'general',
            kindLabel: '일반',
            title: item.title?.trim() || '(제목 없음)',
            dateLabel: formatIncompleteReportDate(item.dueDate),
            sortDate: item.dueDate || '',
            accentColor: null,
            coverImageId: item.coverImageId,
            targets: [target],
          });
          continue;
        }

        if (item.kind === 'project') {
          const accent = (item.color || '').trim() || '#FC5555';
          const projectTitle = item.title?.trim() || '(제목 없음)';
          for (const sub of item.subTasks) {
            const target: IncompleteStatusTarget = {
              chipKey: `p:${item.id}:${sub.id}`,
              kind: 'project',
              itemId: item.id,
              subTaskId: sub.id,
              basePoints: sub.points,
            };
            const status = resolveIncompleteTargetStatus(m.id, target);
            if (isCompletedStatus(status)) continue;
            const subTitle = sub.title?.trim() || '하위과제';
            cards.push({
              itemId: item.id,
              kind: 'project',
              kindLabel: '프로젝트',
              title: `${projectTitle} / ${subTitle}`,
              dateLabel: formatIncompleteReportDate(item.dueDate),
              sortDate: item.dueDate || '',
              /** 프로젝트 설정 색상(프로젝트 색) */
              accentColor: accent,
              coverImageId: item.coverImageId,
              targets: [target],
            });
          }
          continue;
        }

        if (item.kind === 'routine') {
          const today = getRoutineTodayDate(item);
          if (!today) continue;
          const target: IncompleteStatusTarget = {
            chipKey: `r:${item.id}:${today}`,
            kind: 'routine',
            itemId: item.id,
            date: today,
            basePoints: item.basePoints,
          };
          const status = resolveIncompleteTargetStatus(m.id, target);
          if (isCompletedStatus(status)) continue;
          cards.push({
            itemId: item.id,
            kind: 'routine',
            kindLabel: '루틴',
            title: item.title?.trim() || '(제목 없음)',
            dateLabel: formatIncompleteReportDate(today),
            sortDate: today,
            accentColor: INCOMPLETE_ROUTINE_BADGE_BG,
            coverImageId: item.coverImageId,
            targets: [target],
          });
        }
      }

      if (cards.length > 0) {
        cards.sort(
          (a, b) =>
            a.sortDate.localeCompare(b.sortDate) ||
            a.title.localeCompare(b.title, 'ko')
        );
      }
      rows.push({
        memberId: m.id,
        orderIndex: m.orderIndex,
        memberName: displayClassMemberName(m).trim() || '(이름 없음)',
        cards,
      });
    }

    return rows.sort((a, b) => a.orderIndex - b.orderIndex);
  }, [doc.items, members, resolveIncompleteTargetStatus]);

  const applyIncompleteCardStatus = useCallback(
    (
      memberId: string,
      card: IncompleteAssignmentCard,
      newStatus: AssignmentStatusValue
    ) => {
      for (const target of card.targets) {
        const chip = chipFromIncompleteTarget(target);
        if (chip) setMemberStatus(memberId, chip, newStatus);
      }
    },
    [chipFromIncompleteTarget, setMemberStatus]
  );

  useEffect(() => {
    if (!incompleteByMemberOpen) {
      setEditingIncompleteCardKey(null);
      return;
    }
    if (!editingIncompleteCardKey) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest('[data-incomplete-edit-card]')) return;
      setEditingIncompleteCardKey(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [incompleteByMemberOpen, editingIncompleteCardKey]);

  const applyBatchStatus = useCallback(
    (newStatus: AssignmentStatusValue) => {
      if (!selectedChip || selectedStudents.size === 0) return;
      selectedStudents.forEach((id) => {
        if (selectedChip.assignedMemberIds.includes(id)) {
          setMemberStatus(id, selectedChip, newStatus);
        }
      });
      setSelectedStudents(new Set());
    },
    [selectedChip, selectedStudents, setMemberStatus]
  );
  applyBatchRef.current = applyBatchStatus;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT'
      ) {
        return;
      }
      if (selectedStudentsRef.current.size === 0 || !selectedChipKey) return;
      const map: Record<string, AssignmentStatusValue> = {
        '1': 'pending',
        '2': 'confirmed',
        '3': 'delayed',
        '4': 'exempted',
      };
      const status = map[e.key];
      if (!status) return;
      e.preventDefault();
      applyBatchRef.current(status);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedChipKey]);

  const toggleStudentSelect = (memberId: string) => {
    if (!selectedChip?.assignedMemberIds.includes(memberId)) return;
    setSelectedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (!selectedChip) return;
    setSelectedStudents(new Set(selectedChip.assignedMemberIds));
  };

  const handleDeselectAll = () => {
    setSelectedStudents(new Set());
  };

  const toggleViewMode = () => {
    if (viewMode === 'seat') {
      setViewMode('number');
      saveNoteMemberLayoutMode('assignmentPoints', workspaceId, gradeId, 'number');
      return;
    }
    if (!hasSeatedMembers) {
      showInfo(SEAT_EMPTY_TOAST);
      return;
    }
    setViewMode('seat');
    saveNoteMemberLayoutMode('assignmentPoints', workspaceId, gradeId, 'seat');
  };

  const resolveChipFromKey = useCallback(
    (chipKey: string): AssignmentChip | undefined => {
      const found = chips.find((c) => c.key === chipKey);
      if (found) return found;
      if (chipKey.startsWith('g:')) {
        const itemId = chipKey.slice(2);
        const item = doc.items.find((x) => x.id === itemId);
        if (item?.kind === 'general') {
          return {
            key: chipKey,
            kind: 'general',
            itemId,
            title: item.title,
            dueDate: item.dueDate,
            color: '#a2a2a2',
            basePoints: item.basePoints,
            assignedMemberIds: item.assignedMemberIds,
          };
        }
      }
      if (chipKey.startsWith('p:')) {
        const [, itemId, subTaskId] = chipKey.split(':');
        const item = doc.items.find((x) => x.id === itemId);
        if (item?.kind === 'project' && subTaskId) {
          const sub = item.subTasks.find((s) => s.id === subTaskId);
          return {
            key: chipKey,
            kind: 'project',
            itemId,
            subTaskId,
            title: sub?.title || '',
            dueDate: item.dueDate,
            color: item.color,
            basePoints: sub?.points ?? 0,
            assignedMemberIds: item.assignedMemberIds,
          };
        }
      }
      if (chipKey.startsWith('r:')) {
        const [, itemId, date] = chipKey.split(':');
        const item = doc.items.find((x) => x.id === itemId);
        if (item?.kind === 'routine' && date) {
          return {
            key: chipKey,
            kind: 'routine',
            itemId,
            date,
            title: item.title,
            dueDate: date,
            color:
              (item.categoryId
                ? categories.find((c) => c.id === item.categoryId)?.color
                : undefined) || INCOMPLETE_ROUTINE_BADGE_BG,
            basePoints: item.basePoints,
            assignedMemberIds: item.assignedMemberIds,
          };
        }
      }
      return undefined;
    },
    [categories, chips, doc.items]
  );

  const handleSave = async () => {
    if (pendingChanges.size === 0 || saving) return;
    setSaving(true);
    showSaving();
    try {
      const now = new Date().toISOString();
      const touchedIds = new Set<string>();

      type Patch = {
        memberId: string;
        change: PendingChange;
        subTaskId?: string;
        date?: string;
      };
      const generalPatches = new Map<string, Patch[]>();
      const projectPatches = new Map<string, Patch[]>();
      const routinePatches = new Map<string, Patch[]>();

      pendingChanges.forEach((change, ck) => {
        const [memberId, chipKey] = ck.split('::');
        const chip = resolveChipFromKey(chipKey);
        if (!chip) return;
        touchedIds.add(chip.itemId);
        const patch: Patch = {
          memberId,
          change,
          subTaskId: chip.subTaskId,
          date: chip.date,
        };
        if (chip.kind === 'general') {
          const list = generalPatches.get(chip.itemId) ?? [];
          list.push(patch);
          generalPatches.set(chip.itemId, list);
        } else if (chip.kind === 'project') {
          const list = projectPatches.get(chip.itemId) ?? [];
          list.push(patch);
          projectPatches.set(chip.itemId, list);
        } else {
          const list = routinePatches.get(chip.itemId) ?? [];
          list.push(patch);
          routinePatches.set(chip.itemId, list);
        }
      });

      const nextDoc: AssignmentPointsNoteDoc = {
        ...doc,
        updatedAt: now,
        items: doc.items.map((item) => {
          if (item.kind === 'general') {
            const patches = generalPatches.get(item.id);
            if (!patches?.length) return item;
            let statuses = [...item.studentStatuses];
            for (const { memberId, change } of patches) {
              const prev = statuses.find((s) => s.studentId === memberId);
              statuses = upsertStatus(statuses, {
                ...prev,
                studentId: memberId,
                status: change.status,
                earnedPoints: change.points,
                memo: change.memo ?? prev?.memo,
                updatedAt: now,
              });
            }
            return { ...item, studentStatuses: statuses, updatedAt: now };
          }
          if (item.kind === 'project') {
            const patches = projectPatches.get(item.id);
            if (!patches?.length) return item;
            const nextMap = { ...item.studentStatusesBySubTask };
            for (const { memberId, change, subTaskId } of patches) {
              if (!subTaskId) continue;
              const prev = nextMap[`${subTaskId}:${memberId}`];
              nextMap[`${subTaskId}:${memberId}`] = {
                ...prev,
                studentId: memberId,
                status: change.status,
                earnedPoints: change.points,
                memo: change.memo ?? prev?.memo,
                updatedAt: now,
              };
            }
            return { ...item, studentStatusesBySubTask: nextMap, updatedAt: now };
          }
          if (item.kind === 'routine') {
            const patches = routinePatches.get(item.id);
            if (!patches?.length) return item;
            let dateStatuses = [...ensureRoutineDateBuckets(item).dateStatuses];
            for (const { memberId, change, date } of patches) {
              if (!date) continue;
              const idx = dateStatuses.findIndex((b) => b.date === date);
              const prevBucket =
                idx >= 0
                  ? dateStatuses[idx]
                  : { date, studentStatuses: [] as AssignmentStudentStatus[] };
              const prevSt = prevBucket.studentStatuses.find(
                (s) => s.studentId === memberId
              );
              const statuses = upsertStatus(prevBucket.studentStatuses, {
                ...prevSt,
                studentId: memberId,
                status: change.status,
                earnedPoints: change.points,
                memo: change.memo ?? prevSt?.memo,
                updatedAt: now,
              });
              const bucket = { ...prevBucket, date, studentStatuses: statuses };
              if (idx >= 0) dateStatuses[idx] = bucket;
              else dateStatuses = [...dateStatuses, bucket];
            }
            return { ...item, dateStatuses, updatedAt: now };
          }
          return item;
        }),
      };

      await saveAssignmentPointsNote(nextDoc, {
        memberGroupMap,
        syncRewardGroupId: selectedGroupId ?? undefined,
        syncRewardItemIds: Array.from(touchedIds),
      });
      setDoc(nextDoc);
      setPendingChanges(new Map());
      setSelectedStudents(new Set());
      showSuccess('저장되었습니다.');
    } catch (e) {
      showError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const filterSummary = (() => {
    const status = listFilter === 'all' ? '전체 과제' : '미완료 과제';
    if (filterTab === 'allIncomplete') return status;
    if (filterTab === 'general') {
      const cat =
        categoryId === 'all'
          ? '전체 카테고리'
          : categories.find((c) => c.id === categoryId)?.name || '카테고리';
      return `${status} · ${cat}`;
    }
    if (filterTab === 'project') {
      const proj =
        projectId === 'all'
          ? '모든 프로젝트'
          : projectOptions.find((p) => p.id === projectId)?.title || '프로젝트';
      return `${status} · ${proj}`;
    }
    return `${status} · ${formatDateMenuLabel(routineDate)}`;
  })();

  const filterDotColor = (() => {
    if (filterTab === 'general') {
      if (categoryId === 'all') return 'rgba(162, 162, 162, 1)';
      return categories.find((c) => c.id === categoryId)?.color || 'rgba(162, 162, 162, 1)';
    }
    if (filterTab === 'routine') return INCOMPLETE_ROUTINE_BADGE_BG;
    return 'rgba(162, 162, 162, 1)';
  })();

  const renderMemberCard = (m: FlatMember, opts?: { seat?: boolean }) => {
    const seat = opts?.seat === true;
    const isAssigned = selectedChip
      ? selectedChip.assignedMemberIds.includes(m.id)
      : false;
    const disabled = !selectedChip || !isAssigned;
    const unassigned = !!selectedChip && !isAssigned;
    const status = selectedChip
      ? resolveDisplayStatus(m.id, selectedChip)
      : 'pending';
    const hasChange =
      !!selectedChip &&
      pendingChanges.has(cellKey(m.id, selectedChip.key));
    const isSelected = selectedStudents.has(m.id);

    return (
      <div
        key={m.id}
        className={`cp-apn-card-student${hasChange ? ' is-changed' : ''}${
          isSelected ? ' is-selected' : ''
        }${disabled && !unassigned ? ' is-disabled' : ''}${
          unassigned ? ' is-unassigned' : ''
        }${seat ? ' is-seat' : ''}`}
      >
        <div className={`cp-apn-card-student-layout${seat ? ' is-seat' : ''}`}>
          <button
            type="button"
            className={`cp-apn-card-profile${seat ? ' is-seat' : ''}${
              unassigned ? ' is-unassigned' : ''
            }`}
            disabled={disabled}
            onClick={() => toggleStudentSelect(m.id)}
            aria-label={`${displayClassMemberName(m).trim() || '멤버'} 선택`}
          >
            <MemberProfileThumb
              member={m}
              workspaceId={workspaceId}
              gradeId={gradeId}
              className="cp-apn-card-profile-img"
              fallback={
                <span className="cp-apn-card-profile-fallback" aria-hidden>
                  {(displayClassMemberName(m).trim() || '?').slice(0, 1)}
                </span>
              }
            />
            {isSelected ? (
              <span className="cp-apn-card-profile-check" aria-hidden>
                <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                  <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong, #10504a)" />
                  <path
                    d="M10 16L14 20L22 12"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            ) : null}
          </button>

          <div className={`cp-apn-card-student-info${seat ? ' is-seat' : ''}`}>
            <div
              className={`cp-apn-card-student-meta${
                unassigned ? ' is-unassigned' : ''
              }`}
            >
              <span className="cp-apn-card-student-num">
                {formatNum(m.orderIndex)}
              </span>
              <span className="cp-apn-card-student-name">
                {displayClassMemberName(m).trim() || '(이름 없음)'}
              </span>
            </div>
            <div className={`cp-apn-card-status-row${seat ? ' is-seat' : ''}`}>
              {STATUS_ORDER.map((st, index) => {
                const active = status === st;
                const legend = STATUS_LEGEND.find((s) => s.value === st);
                return (
                  <button
                    key={st}
                    type="button"
                    className={`cp-apn-card-status-btn${
                      active ? ' is-active' : ''
                    }`}
                    style={
                      active
                        ? {
                            background: legend?.color,
                            color: st === 'exempted' ? '#000' : '#fff',
                          }
                        : undefined
                    }
                    disabled={disabled}
                    onClick={() => {
                      if (!selectedChip || disabled) return;
                      setMemberStatus(m.id, selectedChip, st);
                    }}
                  >
                    {disabled
                      ? index + 1
                      : active
                        ? STATUS_BTN_LABEL[st] ?? index + 1
                        : index + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="cp-apn-card-panel cp-pin-pres-panel">
        <div className="cp-apn-card-empty">불러오는 중…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="cp-apn-card-panel cp-pin-pres-panel">
        <div className="cp-apn-card-empty">{loadError}</div>
      </div>
    );
  }

  return (
    <div className="cp-apn-card-panel cp-pin-pres-panel">
      <div className="cp-apn-card-top">
        <NoteGroupSelector
          groups={groups}
          selectedGroupId={selectedGroupId}
          onChange={(id) => {
            setSelectedGroupId(id);
            saveNoteSelectedGroupId('assignmentPoints', workspaceId, gradeId, id);
            setSelectedStudents(new Set());
            setSelectedChipKey(null);
          }}
        />
        {filterTab !== 'allIncomplete' && !incompleteByMemberOpen ? (
          <div className="cp-apn-list-filter cp-apn-card-top-filter">
            <button
              type="button"
              className="cp-apn-list-filter-btn"
              aria-expanded={filterMenuOpen}
              onClick={() => setFilterMenuOpen((v) => !v)}
            >
              <span
                className="cp-apn-list-filter-dot"
                style={{ background: filterDotColor }}
                aria-hidden
              />
              <span>{filterSummary}</span>
            </button>
            {filterMenuOpen ? (
              <>
                <div
                  className="cp-apn-list-filter-overlay"
                  role="presentation"
                  onClick={() => setFilterMenuOpen(false)}
                />
                <div
                  className="cp-apn-list-filter-menu cp-apn-card-top-filter-menu"
                  role="listbox"
                >
                  <button
                    type="button"
                    className={`cp-apn-list-filter-option${
                      listFilter === 'all' ? ' is-selected' : ''
                    }`}
                    onClick={() => {
                      setListFilter('all');
                      setFilterMenuOpen(false);
                    }}
                  >
                    전체 과제
                  </button>
                  <button
                    type="button"
                    className={`cp-apn-list-filter-option${
                      listFilter === 'incomplete' ? ' is-selected' : ''
                    }`}
                    onClick={() => {
                      setListFilter('incomplete');
                      setFilterMenuOpen(false);
                    }}
                  >
                    미완료 과제
                  </button>
                  {filterTab === 'general' ? (
                    <>
                      <div className="cp-apn-list-filter-divider" />
                      <button
                        type="button"
                        className={`cp-apn-list-filter-option${
                          categoryId === 'all' ? ' is-selected' : ''
                        }`}
                        onClick={() => {
                          setCategoryId('all');
                          setFilterMenuOpen(false);
                        }}
                      >
                        <span
                          className="cp-apn-list-filter-dot"
                          style={{ background: 'rgba(162, 162, 162, 1)' }}
                          aria-hidden
                        />
                        전체 카테고리
                      </button>
                      {categories.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={`cp-apn-list-filter-option${
                            categoryId === c.id ? ' is-selected' : ''
                          }`}
                          onClick={() => {
                            setCategoryId(c.id);
                            setFilterMenuOpen(false);
                          }}
                        >
                          <span
                            className="cp-apn-list-filter-dot"
                            style={{ background: c.color }}
                            aria-hidden
                          />
                          {c.name}
                        </button>
                      ))}
                    </>
                  ) : null}
                  {filterTab === 'project' ? (
                    <>
                      <div className="cp-apn-list-filter-divider" />
                      <button
                        type="button"
                        className={`cp-apn-list-filter-option${
                          projectId === 'all' ? ' is-selected' : ''
                        }`}
                        onClick={() => {
                          setProjectId('all');
                          setFilterMenuOpen(false);
                        }}
                      >
                        <span className="cp-apn-list-filter-dot" aria-hidden />
                        모든 프로젝트
                      </button>
                      {projectOptions.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={`cp-apn-list-filter-option${
                            projectId === p.id ? ' is-selected' : ''
                          }`}
                          onClick={() => {
                            setProjectId(p.id);
                            setFilterMenuOpen(false);
                          }}
                        >
                          <span className="cp-apn-list-filter-dot" aria-hidden />
                          {p.title}
                        </button>
                      ))}
                    </>
                  ) : null}
                  {filterTab === 'routine' ? (
                    <>
                      <div className="cp-apn-list-filter-divider" />
                      {routineDateOptions.map((iso) => (
                        <button
                          key={iso}
                          type="button"
                          className={`cp-apn-list-filter-option${
                            routineDate === iso ? ' is-selected' : ''
                          }`}
                          onClick={() => {
                            setRoutineDate(iso);
                            setFilterMenuOpen(false);
                          }}
                        >
                          <span className="cp-apn-list-filter-dot" aria-hidden />
                          {formatDateMenuLabel(iso)}
                        </button>
                      ))}
                    </>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="cp-apn-card-tabs cp-apn-card-tabs--5" role="tablist" aria-label="과제 유형">
        <button
          type="button"
          role="tab"
          aria-selected={incompleteByMemberOpen}
          className={`cp-apn-card-tab${incompleteByMemberOpen ? ' is-active' : ''}`}
          onClick={() => {
            setIncompleteByMemberOpen(true);
            setFilterMenuOpen(false);
            setSelectedStudents(new Set());
            setSelectedChipKey(null);
          }}
        >
          멤버별 미완료 과제
        </button>
        {(
          [
            ['allIncomplete', '미완료 과제 전체'],
            ['general', '일반 과제'],
            ['project', '프로젝트 과제'],
            ['routine', '루틴 과제'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={!incompleteByMemberOpen && filterTab === key}
            className={`cp-apn-card-tab${
              !incompleteByMemberOpen && filterTab === key ? ' is-active' : ''
            }`}
            onClick={() => {
              setIncompleteByMemberOpen(false);
              setFilterTab(key);
              setFilterMenuOpen(false);
              setSelectedStudents(new Set());
              setSelectedChipKey(null);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {incompleteByMemberOpen ? (
        <div className="cp-apn-incomplete-inline" role="region" aria-label="멤버별 미완료 과제">
          <div className="cp-apn-incomplete-export">
            <div className="cp-apn-incomplete-export-body">
              {incompleteByMemberRows.length === 0 ? (
                <p className="cp-apn-incomplete-empty">멤버 데이터가 없습니다.</p>
              ) : (
                <div className="cp-apn-incomplete-list">
                  {incompleteByMemberRows.map((row) => {
                    const member = members.find((m) => m.id === row.memberId);
                    const noIncomplete = row.cards.length === 0;
                    return (
                      <div
                        key={row.memberId}
                        className={`cp-apn-incomplete-row${
                          noIncomplete ? ' is-empty' : ''
                        }`}
                        aria-disabled={noIncomplete || undefined}
                      >
                        <div className="cp-apn-incomplete-member">
                          <div className="cp-apn-incomplete-profile" aria-hidden>
                            <MemberProfileThumb
                              member={member}
                              workspaceId={workspaceId}
                              gradeId={gradeId}
                              className="cp-apn-incomplete-profile-img"
                              fallback={
                                <span className="cp-apn-incomplete-profile-fallback">
                                  {(row.memberName || '?').slice(0, 1)}
                                </span>
                              }
                            />
                          </div>
                          <div className="cp-apn-incomplete-member-text">
                            <span className="cp-apn-incomplete-member-no">
                              {row.orderIndex}번
                            </span>
                            <span className="cp-apn-incomplete-member-name">
                              {row.memberName}
                            </span>
                          </div>
                        </div>
                        <div className="cp-apn-incomplete-cards">
                          {noIncomplete ? (
                            <span className="cp-apn-incomplete-cards-empty">
                              미완료 과제 없음
                            </span>
                          ) : (
                            row.cards.map((card) => {
                            const chipKey = card.targets[0]?.chipKey ?? card.itemId;
                            const editKey = `${row.memberId}::${chipKey}`;
                            const editing = editingIncompleteCardKey === editKey;
                            const hasChange = card.targets.some((t) =>
                              pendingChanges.has(cellKey(row.memberId, t.chipKey))
                            );
                            const statuses = card.targets.map((t) =>
                              resolveIncompleteTargetStatus(row.memberId, t)
                            );
                            const activeStatus =
                              statuses.length > 0 &&
                              statuses.every((s) => s === statuses[0])
                                ? statuses[0]
                                : statuses.find((s) => !isCompletedStatus(s)) ??
                                  statuses[0] ??
                                  'pending';
                            const coverImage = card.coverImageId
                              ? coverImageById.get(card.coverImageId)
                              : undefined;

                            return (
                              <div
                                key={`${row.memberId}-${chipKey}`}
                                className={`cp-apn-incomplete-card${
                                  editing ? ' is-editing' : ''
                                }${hasChange ? ' is-changed' : ''}`}
                                data-incomplete-edit-card=""
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  if (card.targets.length === 0) return;
                                  setEditingIncompleteCardKey(editKey);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (card.targets.length === 0) return;
                                    setEditingIncompleteCardKey(editKey);
                                  }
                                }}
                              >
                                {editing ? (
                                  <div
                                    className="cp-apn-incomplete-card-status-grid"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {STATUS_ORDER.map((st, index) => {
                                      const active = activeStatus === st;
                                      const legend = STATUS_LEGEND.find(
                                        (s) => s.value === st
                                      );
                                      return (
                                        <button
                                          key={st}
                                          type="button"
                                          className={`cp-apn-incomplete-status-btn${
                                            active ? ' is-active' : ''
                                          }`}
                                          style={
                                            active
                                              ? {
                                                  background: legend?.color,
                                                  color:
                                                    st === 'exempted'
                                                      ? '#000'
                                                      : '#fff',
                                                }
                                              : undefined
                                          }
                                          onClick={() => {
                                            applyIncompleteCardStatus(
                                              row.memberId,
                                              card,
                                              st
                                            );
                                          }}
                                          aria-label={legend?.label ?? st}
                                        >
                                          {active
                                            ? STATUS_BTN_LABEL[st] ?? index + 1
                                            : index + 1}
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <>
                                    <span className="cp-apn-incomplete-card-cover" aria-hidden>
                                      {coverImage ? (
                                        <CachedAssignmentCoverImage
                                          image={coverImage}
                                          className="cp-apn-incomplete-card-cover-img"
                                          cacheOnly
                                          fallback={
                                            <span className="cp-apn-incomplete-card-cover-placeholder" />
                                          }
                                        />
                                      ) : (
                                        <span className="cp-apn-incomplete-card-cover-placeholder" />
                                      )}
                                    </span>
                                    <div className="cp-apn-incomplete-card-body">
                                    <div className="cp-apn-incomplete-card-meta">
                                      <span
                                        className="cp-apn-incomplete-badge"
                                        style={
                                          card.kind === 'project'
                                            ? {
                                                background:
                                                  card.accentColor || '#FC5555',
                                              }
                                            : incompleteKindBadgeStyle(
                                                card.accentColor
                                              )
                                        }
                                      >
                                        {card.kindLabel}
                                      </span>
                                      <span className="cp-apn-incomplete-date">
                                        {card.dateLabel}
                                      </span>
                                    </div>
                                    <div className="cp-apn-incomplete-card-title">
                                      {card.title}
                                    </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="cp-apn-card-chips-scroll">
            <div className="cp-apn-card-chips-track">
              <div className="cp-apn-card-chips">
              {chips.length === 0 ? (
                <span className="cp-apn-card-chips-empty">
                  {filterTab === 'allIncomplete'
                    ? '미완료 과제가 없습니다.'
                    : filterTab === 'routine'
                      ? '해당 날짜에 진행하는 루틴 과제가 없습니다.'
                      : listFilter === 'incomplete'
                        ? '미완료 과제가 없습니다.'
                        : '과제가 없습니다.'}
                </span>
              ) : (
                chips.map((chip) => {
                  const selected = chip.key === selectedChipKey;
                  const incomplete = chipIsIncomplete(
                    chip,
                    (id, c) => resolveBaseStatus(id, c)?.status ?? 'pending'
                  );
                  const label =
                    chip.kind === 'project' && chip.projectTitle
                      ? `${chip.projectTitle} / ${chip.title}`
                      : chip.title;
                  const coverImage = chip.coverImageId
                    ? coverImageById.get(chip.coverImageId)
                    : undefined;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      className={`cp-apn-card-chip${selected ? ' is-selected' : ''}${
                        !incomplete ? ' is-complete' : ''
                      }`}
                      style={{ ['--cp-apn-card-chip-bar' as string]: chip.color }}
                      onClick={() => {
                        setSelectedChipKey(chip.key);
                        setSelectedStudents(new Set());
                      }}
                      title={label}
                    >
                      <span className="cp-apn-card-chip-icon" aria-hidden>
                        {coverImage ? (
                          <CachedAssignmentCoverImage
                            image={coverImage}
                            className="cp-apn-card-chip-icon-img"
                            cacheOnly
                            fallback={<span className="cp-apn-card-chip-icon-placeholder" />}
                          />
                        ) : (
                          <span className="cp-apn-card-chip-icon-placeholder" />
                        )}
                      </span>
                      <span className="cp-apn-card-chip-text">
                        <span className="cp-apn-card-chip-title">{label}</span>
                        {chip.dueDate ? (
                          <span className="cp-apn-card-chip-date">
                            {formatChipDate(chip.dueDate)}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })
              )}
              </div>
            </div>
          </div>

          <div className="cp-apn-card-body">
          <div className="cp-apn-card-toolbar">
            <div className="cp-apn-card-controls">
              <button
                type="button"
                className={`cp-apn-card-view-toggle${
                  viewMode === 'seat' ? ' is-seat' : ''
                }`}
                onClick={toggleViewMode}
                aria-label="번호 자리 보기 전환"
              >
                <span className={viewMode === 'number' ? 'is-active' : ''}>번호</span>
                <span className={viewMode === 'seat' ? 'is-active' : ''}>자리</span>
              </button>
              <div className="cp-apn-card-select-btns">
                <button
                  type="button"
                  disabled={!selectedChip}
                  onClick={handleSelectAll}
                >
                  전체선택
                </button>
                <button
                  type="button"
                  disabled={!selectedChip}
                  onClick={handleDeselectAll}
                >
                  선택취소
                </button>
              </div>
            </div>
            <div className="cp-apn-card-legend" role="group" aria-label="상태 변경">
              {STATUS_LEGEND.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className="cp-apn-tbl-legend-btn"
                  disabled={!selectedChip || selectedStudents.size === 0}
                  onClick={() => applyBatchStatus(opt.value)}
                >
                  <span
                    className="cp-apn-tbl-legend-num"
                    data-status={opt.value}
                    style={{ background: opt.color }}
                  >
                    {opt.num}
                  </span>
                  <span className="cp-apn-tbl-legend-label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

        <div className="cp-apn-card-members">
          {members.length === 0 ? (
            <div className="cp-apn-card-empty">멤버 데이터가 없습니다.</div>
          ) : viewMode === 'seat' && seatViewData.positioned.length > 0 ? (
            <div className="cp-apn-card-seat-area">
              <div className="cp-apn-card-seat-scroll">
                <div
                  className="cp-apn-card-seat-grid"
                  style={{
                    gridTemplateColumns: seatViewData.colTemplate,
                    gridTemplateRows: seatViewData.rowTemplate,
                  }}
                >
                  {seatViewData.positioned.map(({ member, gridRow, gridCol }) => (
                    <div
                      key={member.id}
                      className="cp-apn-card-seat-cell"
                      style={{
                        gridRow: `${gridRow} / span 2`,
                        gridColumn: `${gridCol} / span 2`,
                      }}
                    >
                      {renderMemberCard(member, { seat: true })}
                    </div>
                  ))}
                </div>
              </div>
              <div className="cp-apn-card-seat-rotate-wrap">
                <button
                  type="button"
                  className="cp-apn-card-seat-rotate-btn"
                  title="180도 회전"
                  aria-label="자리 배치 180도 회전"
                  onClick={() => setIsSeatRotated((v) => !v)}
                >
                  <span className="cp-apn-card-seat-rotate-arrows" aria-hidden>
                    <SeatRotateIcon size={16} />
                  </span>
                  <span>180</span>
                </button>
              </div>
              {seatViewData.unseated.length > 0 ? (
                <div className="cp-apn-card-unseated">
                  <div className="cp-apn-card-unseated-title">자리 미지정 멤버</div>
                  <div className="cp-apn-card-grid">
                    {seatViewData.unseated.map((m) =>
                      renderMemberCard(m, { seat: true })
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="cp-apn-card-grid">
              {members.map((m) => renderMemberCard(m))}
            </div>
          )}
        </div>
      </div>
        </>
      )}

      <button
        type="button"
        className={`cp-apn-card-fab${hasChanges && !saving ? '' : ' is-disabled'}`}
        disabled={!hasChanges || saving}
        onClick={() => void handleSave()}
        aria-label="저장하기"
        title="저장하기"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 3V8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <AutoStatusToast state={toastState} />
    </div>
  );
};

export default AssignmentManagementCardPanel;
